import React, { useState } from 'react'
import { 
  Code, 
  Layers, 
  Cloud, 
  Database, 
  Settings, 
  Star,
  ChevronRight
} from 'lucide-react'
import { skills } from '../data/portfolio'
import { useScrollAnimation } from '../hooks/useScrollAnimation'
import type { Skill } from '../types'

interface SkillCategoryCardProps {
  category: string
  config: {
    icon: React.ComponentType<any>
    label: string
    color: string
    description: string
  }
  categorySkills: Skill[]
  index: number
  onViewAll: (category: string) => void
}

const SkillCategoryCard: React.FC<SkillCategoryCardProps> = ({ 
  category, 
  config, 
  categorySkills, 
  index, 
  onViewAll 
}) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 })
  const IconComponent = config.icon

  const renderProficiencyStars = (proficiency: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        size={14}
        className={`${
          index < proficiency
            ? 'text-yellow-400 fill-current'
            : 'text-gray-300 dark:text-slate-600'
        }`}
      />
    ))
  }

  return (
    <div 
      ref={ref}
      className={`transition-all duration-700 delay-${index * 100} ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      <div className="bg-gray-50 dark:bg-slate-800 p-6 rounded-xl border border-gray-200 dark:border-slate-700 card-hover h-full">
        <div className="flex items-center space-x-3 mb-4">
          <div className={`p-2 bg-${config.color}-100 dark:bg-${config.color}-900/30 rounded-lg`}>
            <IconComponent className={`text-${config.color}-600 dark:text-${config.color}-400`} size={24} />
          </div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-white">
            {config.label}
          </h3>
        </div>
        
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
          {config.description}
        </p>

        <div className="space-y-3 mb-4">
          {categorySkills.slice(0, 4).map((skill) => (
            <div key={skill.name} className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {skill.name}
              </span>
              <div className="flex space-x-1">
                {renderProficiencyStars(skill.proficiency)}
              </div>
            </div>
          ))}
          {categorySkills.length > 4 && (
            <div className="text-xs text-slate-500 dark:text-slate-400">
              +{categorySkills.length - 4} more skills
            </div>
          )}
        </div>

        <button
          onClick={() => onViewAll(category)}
          className={`w-full flex items-center justify-center space-x-2 py-2 px-4 bg-${config.color}-50 dark:bg-${config.color}-900/20 text-${config.color}-600 dark:text-${config.color}-400 rounded-lg hover:bg-${config.color}-100 dark:hover:bg-${config.color}-900/30 transition-colors duration-200`}
        >
          <span className="text-sm font-medium">View All</span>
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  )
}

interface SkillDetailCardProps {
  skill: Skill
  index: number
  config: {
    color: string
  }
}

const SkillDetailCard: React.FC<SkillDetailCardProps> = ({ skill, index, config }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 })

  const renderProficiencyStars = (proficiency: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        size={14}
        className={`${
          index < proficiency
            ? 'text-yellow-400 fill-current'
            : 'text-gray-300 dark:text-slate-600'
        }`}
      />
    ))
  }

  const renderProficiencyBar = (proficiency: number, color: string) => {
    const percentage = (proficiency / 5) * 100
    return (
      <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2">
        <div 
          className={`h-2 rounded-full bg-${color}-500 transition-all duration-1000 ease-out`}
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
    )
  }

  return (
    <div 
      ref={ref}
      className={`transition-all duration-700 delay-${index * 50} ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      <div className="bg-white dark:bg-slate-800 p-6 rounded-xl border border-gray-200 dark:border-slate-700 card-hover">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-lg font-semibold text-slate-900 dark:text-white">
            {skill.name}
          </h4>
          <div className="flex space-x-1">
            {renderProficiencyStars(skill.proficiency)}
          </div>
        </div>
        
        <div className="mb-2">
          {renderProficiencyBar(skill.proficiency, config.color)}
        </div>
        
        <div className="flex justify-between items-center text-sm">
          <span className="text-slate-500 dark:text-slate-400">
            Proficiency
          </span>
          <span className={`font-medium text-${config.color}-600 dark:text-${config.color}-400`}>
            {skill.proficiency === 5 ? 'Expert' : 
             skill.proficiency === 4 ? 'Advanced' :
             skill.proficiency === 3 ? 'Intermediate' :
             skill.proficiency === 2 ? 'Basic' : 'Beginner'}
          </span>
        </div>
      </div>
    </div>
  )
}

const Skills: React.FC = () => {
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation()
  const [selectedCategory, setSelectedCategory] = useState<string>('all')

  const categoryConfig = {
    languages: {
      icon: Code,
      label: 'Programming Languages',
      color: 'blue',
      description: 'Core programming languages I work with'
    },
    frameworks: {
      icon: Layers,
      label: 'Frameworks & Libraries',
      color: 'purple',
      description: 'Frameworks and libraries for building applications'
    },
    cloud: {
      icon: Cloud,
      label: 'Cloud & DevOps',
      color: 'green',
      description: 'Cloud platforms and DevOps tools'
    },
    databases: {
      icon: Database,
      label: 'Databases',
      color: 'orange',
      description: 'Database technologies and data management'
    },
    tools: {
      icon: Settings,
      label: 'Tools & Others',
      color: 'gray',
      description: 'Development tools and methodologies'
    }
  }

  const categories = Object.keys(categoryConfig) as Array<keyof typeof categoryConfig>
  
  const filteredSkills = selectedCategory === 'all' 
    ? skills 
    : skills.filter(skill => skill.category === selectedCategory)

  const groupedSkills = categories.reduce((acc, category) => {
    acc[category] = skills.filter(skill => skill.category === category)
    return acc
  }, {} as Record<string, typeof skills>)

  return (
    <section id="skills" className="section-padding bg-white dark:bg-slate-900">
      <div className="container-max">
        <div 
          ref={titleRef}
          className={`text-center mb-16 transition-all duration-700 ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-slate-900 dark:text-white">
            Skills & Technologies
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
            A comprehensive overview of my technical expertise across different domains
          </p>
        </div>

        {/* Category Filter */}
        <div className="mb-12">
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                selectedCategory === 'all'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700'
              }`}
            >
              All Skills
            </button>
            {categories.map((category) => {
              const config = categoryConfig[category]
              const IconComponent = config.icon
              return (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    selectedCategory === category
                      ? `bg-${config.color}-600 text-white shadow-md`
                      : 'bg-gray-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700'
                  }`}
                >
                  <IconComponent size={16} />
                  <span>{config.label}</span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Skills Display */}
        {selectedCategory === 'all' ? (
          /* Category Overview */
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categories.map((category, index) => (
              <SkillCategoryCard
                key={category}
                category={category}
                config={categoryConfig[category]}
                categorySkills={groupedSkills[category]}
                index={index}
                onViewAll={setSelectedCategory}
              />
            ))}
          </div>
        ) : (
          /* Detailed Category View */
          <div>
            <div className="mb-8">
              <div className="flex items-center space-x-3 mb-2">
                <div className={`p-2 bg-${categoryConfig[selectedCategory as keyof typeof categoryConfig].color}-100 dark:bg-${categoryConfig[selectedCategory as keyof typeof categoryConfig].color}-900/30 rounded-lg`}>
                  {React.createElement(categoryConfig[selectedCategory as keyof typeof categoryConfig].icon, {
                    className: `text-${categoryConfig[selectedCategory as keyof typeof categoryConfig].color}-600 dark:text-${categoryConfig[selectedCategory as keyof typeof categoryConfig].color}-400`,
                    size: 24
                  })}
                </div>
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white">
                  {categoryConfig[selectedCategory as keyof typeof categoryConfig].label}
                </h3>
              </div>
              <p className="text-slate-600 dark:text-slate-400">
                {categoryConfig[selectedCategory as keyof typeof categoryConfig].description}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSkills.map((skill, index) => (
                <SkillDetailCard
                  key={skill.name}
                  skill={skill}
                  index={index}
                  config={categoryConfig[skill.category]}
                />
              ))}
            </div>
          </div>
        )}

        {/* Skills Summary */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-8 rounded-xl border border-blue-200 dark:border-blue-800">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
              Continuous Learning
            </h3>
            <p className="text-slate-600 dark:text-slate-300 mb-4">
              I'm always expanding my skill set and staying up-to-date with the latest technologies and best practices.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-slate-600 dark:text-slate-400">
                  {skills.length} Total Skills
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-slate-600 dark:text-slate-400">
                  {skills.filter(s => s.proficiency >= 4).length} Advanced+
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                <span className="text-slate-600 dark:text-slate-400">
                  6+ Years Experience
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Skills